import { onMounted, onBeforeUnmount } from "vue";
import {
  decodeCardClass,
  findCardWrapper,
  applyDeletedStyling,
} from "@/views/encounter-notes/utils/actionCardUtils";

/**
 * @param {Object} options
 * @param {import('vue').Ref} options.editor - TipTap editor ref
 * @param {Object} options.props - Component props (isDraft)
 * @param {Function} options.emit - Component emit function
 * @param {Function} options.runSave - Trigger an immediate save
 */
export function useEditorCardActions({ editor, props, emit, runSave }) {

  const insertSlashCommandResult = (content) => {
    const instance = editor.value;
    if (!instance) return;

    if (typeof content === 'object' && content.type === 'cardAction') {
      instance.chain()
        .focus()
        .insertContent({ type: 'cardAction', attrs: content.attrs })
        .run();
      return;
    }

    instance.chain()
      .focus()
      .insertContent(content, { parseOptions: { preserveWhitespace: 'full' } })
      .run();
  };

  const insertCardAction = (attrs) => {
    const instance = editor.value;
    if (!instance) return;

    instance.chain()
      .focus()
      .insertContent([
        { type: 'cardAction', attrs: { ...attrs, freshlyCreated: true } },
        { type: 'paragraph' },
      ])
      .run();
  };

  const updateCardAction = (attrs) => {
    const { type, id, title, description } = attrs;
    if (!type || !id) return;

    const editorElement = document.querySelector('.editor-input');
    if (!editorElement) return;

    const cardWrappers = editorElement.querySelectorAll('.card-action-wrapper');

    for (const wrapper of cardWrappers) {
      const decoded = decodeCardClass(wrapper.className);
      if (decoded && decoded.type === type && String(decoded.id) === String(id)) {
        const titleEl = wrapper.querySelector('.card-action-title');
        if (titleEl) {
          const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
          titleEl.textContent = `${typeLabel}: ${title}`;
        }
        const descEl = wrapper.querySelector('.card-action-description');
        if (descEl) descEl.textContent = description;

        if (type === 'task') {
          wrapper._taskData = { ...wrapper._taskData, ...attrs, task_id: id };
        } else if (type === 'appointment') {
          wrapper._eventData = { ...wrapper._eventData, ...attrs, event_id: id };
        }
        break;
      }
    }
  };

  const removeCardAction = (attrs) => {
    const { type, id } = attrs;
    if (!type || !id) return;

    const instance = editor.value;
    if (!instance) return;

    const { doc } = instance.state;
    let nodeToDelete = null;

    doc.descendants((node, pos) => {
      if (node.type.name === 'cardAction') {
        if (node.attrs.type === type && String(node.attrs.id) === String(id)) {
          nodeToDelete = { pos, size: node.nodeSize };
          return false;
        }
      }
    });

    if (nodeToDelete !== null) {
      instance.chain().focus().deleteRange({ from: nodeToDelete.pos, to: nodeToDelete.pos + nodeToDelete.size }).run();
      runSave();
    }
  };

  const markCardAsCancelled = (attrs) => {
    const { type, id } = attrs;
    if (!type || !id) return;

    const wrapper = findCardWrapper('.editor-input', type, id);
    if (wrapper) {
      applyDeletedStyling(wrapper, type, () => removeCardAction({ type, id }), props.isDraft);
    }
  };

  // Card action button click delegation
  const handleCardActionClick = (event) => {
    const target = event.target.closest('.card-action-btn');
    if (!target) return;

    const action = target.dataset.action;
    const type = target.dataset.type;
    const id = target.dataset.id;
    const calendarId = target.dataset.calendarId;

    if (!action || !type || !id) return;

    const cardWrapper = target.closest('.card-action-wrapper');

    if (type === 'task') {
      const taskData = cardWrapper?._taskData || { task_id: parseInt(id, 10) };
      if (action === 'edit') {
        emit('editTask', { ...taskData, task_id: parseInt(id, 10), mode: 'edit' });
      } else if (action === 'delete') {
        emit('deleteTask', { ...taskData, task_id: parseInt(id, 10) });
      }
    } else if (type === 'appointment') {
      const eventData = cardWrapper?._eventData || { event_id: parseInt(id, 10) };
      if (action === 'edit') {
        emit('editAppointment', { ...eventData, event_id: parseInt(id, 10), calendar_id: calendarId ? parseInt(calendarId, 10) : null });
      } else if (action === 'cancel') {
        emit('cancelAppointment', { ...eventData, event_id: parseInt(id, 10), calendar_id: calendarId ? parseInt(calendarId, 10) : null });
      }
    } else if (type === 'document') {
      const docData = cardWrapper?._documentData || { id };
      if (action === 'preview') {
        emit('previewDocument', docData);
      } else if (action === 'delete') {
        emit('deleteDocument', docData);
      }
    } else if (type === 'order') {
      if (action === 'details') {
        const decoded = cardWrapper ? decodeCardClass(cardWrapper.className) : null;
        emit('seeOrderDetails', {
          accessionId: id,
          sampleId: decoded?.sampleId || cardWrapper?._orderData?.sample_id || '',
          orderData: cardWrapper?._orderData,
        });
      }
    } else if (type === 'form') {
      if (action === 'details') {
        emit('seeFormDetails', { assignmentId: id });
      }
    }
  };

  // Lifecycle: attach/detach click listener
  onMounted(() => {
    const editorElement = document.querySelector('.editor-input');
    if (editorElement) editorElement.addEventListener('click', handleCardActionClick);
  });

  onBeforeUnmount(() => {
    const editorElement = document.querySelector('.editor-input');
    if (editorElement) editorElement.removeEventListener('click', handleCardActionClick);
  });

  return {
    insertSlashCommandResult,
    insertCardAction,
    updateCardAction,
    removeCardAction,
    markCardAsCancelled,
  };
}
